import React from 'react';

import { faFile, faTrashXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropzone, DropzoneProps, MIME_TYPES } from '@mantine/dropzone';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { MimeType } from '../types';

import AddFolderIcon from '../assets/icons/add-folder.svg';
import { cn } from '../utils/classUtils';
import { convertMegaBytesToBytes } from '../utils/FileUtils';
import { getSupportedFormatsText } from '../utils/StringUtils';

type Theme = 'LIGHT' | 'DARK' | 'NAVY';

const ThemeColors: Record<
  Theme,
  {
    background: string;
    textColor: string;
    primaryColor: string;
  }
> = {
  LIGHT: {
    background: '!bg-white',
    textColor: '!text-reskin-primary-dark',
    primaryColor: '!text-reskin-primary-blue',
  },
  DARK: {
    background: '!bg-reskin-regent-900',
    textColor: '!text-reskin-primary-light',
    primaryColor: '!text-reskin-aqua',
  },
  NAVY: {
    background: '!bg-reskin-primary-navy',
    textColor: '!text-reskin-primary-light',
    primaryColor: '!text-reskin-aqua',
  },
};

export interface DragAndDropInputProps
  extends Omit<DropzoneProps, 'maxSize' | 'accept'> {
  /**
   * The maximum file size (in mega bytes) that is allowed to be dropped.
   */
  maxSize?: number;
  /**
   * The theme of the component (LIGHT or DARK).
   */
  theme?: Theme;
  accept?: MimeType[];
  files?: File[];
  showAttachedFiles?: boolean;
  onRemoveFile?: (file: File) => void;
  attachedFilesClassName?: string;
  noBorder?: boolean;
}

const DragAndDropInput: React.FC<DragAndDropInputProps> = ({
  onDrop,
  children,
  maxSize = 20,
  theme = 'LIGHT',
  files,
  showAttachedFiles = true,
  onRemoveFile,
  accept = [MIME_TYPES.png],
  attachedFilesClassName,
  noBorder = false,
  ...rest
}) => {
  const { background, textColor, primaryColor } = ThemeColors[theme];

  const DefaultPlaceholder = (
    <div className='text-sm font-normal flex flex-col items-center gap-2 text-center'>
      <div className='flex items-center gap-1.5'>
        <img src={AddFolderIcon} alt='Add Folder Icon' />
        <p className={textColor}>
          <span className={classNames('font-medium', primaryColor)}>
            Browse Files
          </span>{' '}
          <span>or Drag & Drop</span>
        </p>
      </div>
      <p
        className={cn(
          '!text-opacity-[0.64] flex flex-col items-center',
          textColor,
        )}
      >
        <span>
          Supported formats: {getSupportedFormatsText(accept as MimeType[])}.
        </span>{' '}
        <span>Max. size {maxSize}MB</span>
      </p>
    </div>
  );

  return (
    <div className='font-inter'>
      <Dropzone
        accept={accept}
        classNames={{
          root: cn(
            '!rounded-lg !border !border-reskin-regent-400',
            background,
            {
              '!border-none': noBorder,
            },
          ),
        }}
        onDrop={onDrop}
        maxSize={convertMegaBytesToBytes(maxSize)}
        {...rest}
      >
        {children ? children : DefaultPlaceholder}
      </Dropzone>
      {!isEmpty(files) && showAttachedFiles && (
        <div
          className={cn(
            'font-normal text-sm my-2 p-4 pb-0 pr-0 rounded-lg relative overflow-hidden',
            textColor,
            background,
            {
              'mt-0': theme === 'LIGHT',
            },
            attachedFilesClassName,
          )}
        >
          <p className='mb-2'>Attached documents</p>
          <div className='flex flex-col gap-2 max-h-[134px] pb-4 pr-4 overflow-auto'>
            {files?.map((file) => (
              <div
                key={file.name}
                className='flex items-center justify-between gap-2'
              >
                <div className='flex items-center gap-2 max-w-[90%]'>
                  <FontAwesomeIcon icon={faFile} className={primaryColor} />
                  <p
                    className={cn(
                      '!text-opacity-[0.64] line-clamp-1',
                      textColor,
                    )}
                  >
                    {file.name}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faTrashXmark}
                  className='text-reskin-red-500 cursor-pointer'
                  onClick={() => onRemoveFile?.(file)}
                />
              </div>
            ))}
          </div>
          <div
            className={cn(
              'absolute bottom-0 left-0 w-full h-5 bg-gradient-to-b',
              {
                'from-transparent to-reskin-regent-900': theme === 'DARK',
                'from-transparent to-reskin-primary-light': theme === 'LIGHT',
              },
            )}
          />
        </div>
      )}
    </div>
  );
};

export default DragAndDropInput;
